/** "生成剧本" / Script Generation Canonical Generation Card
 * (VISTOERA_SCRIPT_GENERATION_CANONICAL_CARD_UI_STRICT_VISUAL_ALIGNMENT_V2).
 *
 * Vertical order (HARD): Header → Result → Context/Assets → Parameters →
 * Prompt → Footer. Collapsed = Header + Result only. Business contracts
 * from V1 (Creative Context DnD, Supporting Assets refs, Provider Option,
 * Skill Registry, Generate=1, History) are preserved — this file is a
 * UI/UX reshape, not a backend rewrite. */

import {
  BranchesOutlined,
  CloseOutlined,
  CopyOutlined,
  DownloadOutlined,
  FileTextOutlined,
  HistoryOutlined,
  LoadingOutlined,
  PlusOutlined,
  RightOutlined,
  UserOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
} from '@ant-design/icons'
import { Handle, Position, useUpdateNodeInternals, type NodeProps } from '@xyflow/react'
import { Modal, Tooltip, message } from 'antd'
import CanvasSelect from '../CanvasSelect'
import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent as ReactDragEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react'
import { useTranslation } from 'react-i18next'
import { SCRIPT_GENERATION_CAPABILITY, type ImageAssetPickerSelection, type ScriptArtifact, type ScriptScene } from '../../../../api'
import { patchCanvasFieldsIfChanged } from '../canvasFieldsEqual'
import CanvasFunctionCardHeader from '../CanvasFunctionCardHeader'
import {
  buildExplicitProviderSelectionPatch,
  isProviderOptionSelectable,
  normalizeProviderSelectionMode,
  resolveCapabilityProviderOptionId,
} from '../capabilityProviderSelection'
import { loadImagePickerItemDetailDeduped } from '../canvasSharedConfigLoaders'
import { functionCardShowsInspector } from '../functionCardInteractionV2'
import { NODE_EXPAND_TRIGGER_CLASS, canvasNodeShellStateClass } from '../canvasNodeShellV2'
import {
  computeNeedsRegeneration,
  coalesceScriptSupportingAssets,
  deriveNamedEntityAssetsFromCreative,
  applyBindingsToSupportingAssets,
  inferEntityAssetBindings,
  isIncompleteEntityFragment,
  mergeSupportingAssetsOnCreativeLink,
  partitionScriptEntities,
  type ScriptCreativeContextField,
  type ScriptSupportingAssetField,
} from '../scriptGenerationCanvas'
import { evaluateScriptGenerationReadiness, normalizeScriptGenerationFieldDefaults } from '../scriptGenerationReadiness'
import { primaryBlockingReason } from '../generationReadiness'
import { buildScriptContextDragPayloadFromScriptNode, SCRIPT_CONTEXT_DND_TYPE } from '../storyboardGenerationCanvas'
import CanvasResultQuickActionRail from './CanvasResultQuickActionRail'
import { vistoeraDndProps } from '../canvasDragContract'
import { NODE_CONTEXT_DND_HANDLE_CLASS, NODE_PRESENTATION_SURFACE_CLASS } from '../nodeInteractionContract'
import { CREATIVE_CONTEXT_DND_TYPE } from './CreativeCandidateNode'
import { useNodeDefinition } from '../nodeRegistry'
import { useCapabilityProviderOptions } from '../useCapabilityProviderOptions'
import type { FlowNodeData } from '../types'
import ImageThumbnailHoverPreview from './ImageThumbnailHoverPreview'
import VideoInputSectionHandle from './VideoInputSectionHandle'
import {
  GENERATE_SCRIPT_NODE_AGGREGATE_HANDLE,
  GENERATE_SCRIPT_SOURCE_INPUT_HANDLE,
  resolveScriptSourceContext,
  scriptSourcePreviewText,
  scriptSourceSourceLabel,
} from '../scriptSourceContext'
import { SCRIPT_SOURCE_SECTION_TESTID } from '../scriptInputPorts'

import { generationNodeCardWidth } from '../generationNodeWidthPresets'

const NODE_CARD_WIDTH = generationNodeCardWidth('script_generation')
const ADDITIONAL_REQUIREMENTS_MAX = 500
const ACTIVE_RUN_STATUSES = new Set(['SUBMITTING', 'QUEUED', 'GENERATING'])
const PREVIEW_SCENE_LIMIT = 3

const CONTENT_TYPE_OPTIONS = [
  { value: '广告剧本', label: '广告剧本' },
  { value: '短片剧本', label: '短片剧本' },
  { value: '剧情片', label: '剧情片' },
  { value: '喜剧', label: '喜剧' },
  { value: '科幻', label: '科幻' },
  { value: '其他', label: '其他' },
]

const DURATION_PRESETS = [
  { key: '15', label: '15秒', value: 15 },
  { key: '30', label: '30秒', value: 30 },
  { key: '60', label: '60秒', value: 60 },
  { key: '120', label: '1-3分钟', value: 120 },
] as const

const SCRIPT_STRUCTURE_OPTIONS = [
  { value: '三幕结构（开端-发展-结局）', label: '三幕结构（开端-发展-结局）' },
  { value: '悬念钩子结构（开场即高潮）', label: '悬念钩子结构（开场即高潮）' },
  { value: '痛点-解决方案结构', label: '痛点-解决方案结构' },
  { value: '单场景高密度结构', label: '单场景高密度结构' },
]

const GENERATION_REQUIREMENT_PRESETS = ['突出产品卖点', '强化人物反差萌', '加入剧情反转', '强化情感冲突', '加入悬念钩子', '保持品牌调性']

function formatDuration(seconds: number | null | undefined): string | null {
  if (seconds == null || seconds <= 0) return null
  if (seconds < 60) return `${Math.round(seconds)}秒`
  const minutes = seconds / 60
  return `${minutes % 1 === 0 ? minutes : minutes.toFixed(1)}分钟`
}

function formatTimecode(startSec: number, endSec: number): string {
  const fmt = (s: number) => {
    const m = Math.floor(s / 60)
    const sec = Math.floor(s % 60)
    return `${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`
  }
  return `${fmt(startSec)}–${fmt(endSec)}`
}

function normalizeContentType(raw: string | null | undefined): string | null {
  if (!raw) return null
  if (raw === 'ad_script') return '广告剧本'
  return raw
}

function sceneStartEnd(scenes: ScriptScene[], index: number): { start: number; end: number } {
  let start = 0
  for (let i = 0; i < index; i++) start += Math.max(0, scenes[i]?.duration_sec || 0)
  const end = start + Math.max(0, scenes[index]?.duration_sec || 0)
  return { start, end }
}

function CharacterRefThumb({
  asset,
  workspaceId,
  fallback = 'user',
}: {
  asset: ScriptSupportingAssetField
  workspaceId: string
  fallback?: 'user' | 'object'
}) {
  const [thumb, setThumb] = useState<string | null>(null)
  useEffect(() => {
    let cancelled = false
    const pickerId = asset.picker_item_id || (asset.asset_id ? `sa:${asset.asset_id}` : null)
    if (pickerId && workspaceId) {
      loadImagePickerItemDetailDeduped(workspaceId, pickerId, { include_usage: false, include_variants: false })
        .then((d) => {
          if (!cancelled) setThumb(d.thumbnail_url || d.preview_url || null)
        })
        .catch(() => {})
    }
    return () => {
      cancelled = true
    }
  }, [asset.asset_id, asset.picker_item_id, workspaceId])
  if (!thumb) return fallback === 'object' ? <FileTextOutlined /> : <UserOutlined />
  return <ImageThumbnailHoverPreview thumbUrl={thumb} />
}

function SupportingAssetChip({
  asset,
  workspaceId,
  onRemove,
}: {
  asset: ScriptSupportingAssetField
  workspaceId: string
  onRemove: () => void
}) {
  const [thumb, setThumb] = useState<string | null>(null)
  useEffect(() => {
    let cancelled = false
    if (asset.picker_item_id && workspaceId) {
      loadImagePickerItemDetailDeduped(workspaceId, asset.picker_item_id, { include_usage: false, include_variants: false })
        .then((d) => {
          if (!cancelled) setThumb(d.thumbnail_url || d.preview_url || null)
        })
        .catch(() => {})
    }
    return () => {
      cancelled = true
    }
  }, [asset.picker_item_id, workspaceId])
  return (
    <span className="pcv-brief-asset-chip nodrag" title={`${asset.asset_type} · ${asset.display_name}${asset.boundEntityName ? ` → ${asset.boundEntityName}` : ''}`}>
      <ImageThumbnailHoverPreview thumbUrl={thumb} />
      <span className="pcv-brief-asset-type">{asset.asset_type}</span>
      <span className="pcv-brief-asset-name">{asset.display_name}</span>
      {asset.boundEntityName ? <span className="pcv-script-asset-inherited-dot" title={`绑定：${asset.boundEntityName}`} /> : null}
      {asset.inherited ? <span className="pcv-script-asset-inherited-dot" title="继承自创意" /> : null}
      <button type="button" className="pcv-brief-asset-remove" aria-label="移除" onClick={onRemove}>
        <CloseOutlined />
      </button>
    </span>
  )
}

function HistoryList({
  entries,
}: {
  entries: Array<{ script: ScriptArtifact; version: number; completedAt?: string }>
}) {
  if (entries.length === 0) {
    return <div className="pcv-gen-history-empty">暂无历史版本</div>
  }
  return (
    <div className="pcv-gen-history-list">
      {entries.map((h, idx) => (
        <div key={`${h.version}-${idx}`} className="pcv-gen-history-item">
          <span className="pcv-gen-history-version">V{h.version}</span>
          <div className="pcv-gen-history-body">
            <div className="pcv-gen-history-title">{h.script?.title || '剧本'}</div>
            <div className="pcv-gen-history-meta">
              <span>{h.script?.scenes?.length || 0} 场</span>
              <span>{formatDuration(h.script?.target_duration_sec) || '—'}</span>
              <span>{h.completedAt ? new Date(h.completedAt).toLocaleString() : ''}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  )
}

function ScriptFullDetailModal({
  script,
  open,
  onClose,
}: {
  script: ScriptArtifact | null
  open: boolean
  onClose: () => void
}) {
  if (!script) return null
  const handleDownload = () => {
    const blob = new Blob([JSON.stringify(script, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${script.title || 'script'}.json`
    a.click()
    URL.revokeObjectURL(url)
  }
  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={script.title}
      width={760}
      footer={
        <div className="pcv-script-viewer-footer">
          <button type="button" className="pcv-candidate-action-btn" onClick={handleDownload}>
            <DownloadOutlined /> 下载 JSON
          </button>
          <button type="button" className="pcv-candidate-action-btn pcv-candidate-action-btn--primary" onClick={onClose}>
            关闭
          </button>
        </div>
      }
      destroyOnClose
    >
      <div className="pcv-script-detail-modal">
        <p>
          <strong>Logline：</strong>
          {script.logline || '—'}
        </p>
        <p>
          <strong>梗概：</strong>
          {script.synopsis || '—'}
        </p>
        <p>
          <strong>角色：</strong>
          {(script.characters || []).join('、') || '—'}
        </p>
        <p>
          <strong>基调 / 节奏：</strong>
          {[script.tone, script.pacing].filter(Boolean).join(' · ') || '—'}
        </p>
        {(script.scenes || []).map((scene) => (
          <div key={scene.scene_number} className="pcv-script-detail-scene">
            <div className="pcv-script-detail-scene-header">
              第 {scene.scene_number} 场 · {scene.setting} · {formatDuration(scene.duration_sec)}
            </div>
            {scene.dramatic_purpose ? <div className="pcv-script-detail-scene-purpose">{scene.dramatic_purpose}</div> : null}
            {scene.action ? <div className="pcv-script-detail-scene-action">{scene.action}</div> : null}
            {(scene.dialogue || []).map((line, idx) => (
              <div key={idx} className="pcv-script-detail-scene-dialogue">
                <strong>{line.character}：</strong>
                {line.line}
              </div>
            ))}
            {scene.voiceover ? <div className="pcv-script-detail-scene-vo">旁白：{scene.voiceover}</div> : null}
          </div>
        ))}
        <p>
          <strong>结尾：</strong>
          {script.ending || '—'}
        </p>
        {script.key_product_moments?.length ? (
          <p>
            <strong>产品关键时刻：</strong>
            {script.key_product_moments.join('、')}
          </p>
        ) : null}
      </div>
    </Modal>
  )
}

function ScriptResultPresentation({
  script,
  contentType,
  showExpanded,
  onOpenFull,
  draggable,
  onDragStart,
  supportingAssets = [],
  workspaceId = '',
}: {
  script: ScriptArtifact
  contentType: string | null
  showExpanded: boolean
  onOpenFull: () => void
  draggable?: boolean
  onDragStart?: (e: ReactDragEvent) => void
  supportingAssets?: ScriptSupportingAssetField[]
  workspaceId?: string
}) {
  const scenes = script.scenes || []
  const { characters, keyObjects, characterReferenceByName, productReferenceByName } = useMemo(
    () => partitionScriptEntities(script, supportingAssets),
    [script, supportingAssets],
  )
  const [scenesExpanded, setScenesExpanded] = useState(false)
  const visibleScenes = scenesExpanded ? scenes : scenes.slice(0, PREVIEW_SCENE_LIMIT)
  const tags = useMemo(() => {
    const out: string[] = []
    for (const t of script.tags || []) if (t && !out.includes(t)) out.push(t)
    if (script.tone && !out.includes(script.tone)) out.push(script.tone)
    if (characters.length >= 2 && !out.includes('双主角')) out.push('双主角')
    const dur = formatDuration(script.target_duration_sec)
    if (dur && !out.includes(dur)) out.push(dur)
    if (contentType && !out.includes(contentType)) out.push(contentType)
    return out
  }, [characters.length, contentType, script.tags, script.target_duration_sec, script.tone])

  return (
    <div className={`pcv-script-result-rich ${NODE_PRESENTATION_SURFACE_CLASS}`}>
      <div className="pcv-script-result-hero">
        <div className="pcv-script-result-poster" aria-hidden>
          <FileTextOutlined />
          <span>剧本</span>
        </div>
        <div className="pcv-script-result-hero-body">
          <div className="pcv-script-result-title-row">
            <span className="pcv-script-result-title">{script.title}</span>
            <span className="pcv-script-result-badge">剧本</span>
            {draggable ? (
              <FileTextOutlined
                className={`pcv-candidate-drag-hint ${NODE_CONTEXT_DND_HANDLE_CLASS}`}
                {...vistoeraDndProps('script-context')}
                draggable
                onDragStart={onDragStart}
                title="拖拽到「生成分镜」节点，作为其剧本上下文"
              />
            ) : null}
          </div>
          {script.logline || script.synopsis ? (
            <div className="pcv-script-result-synopsis">{script.logline || script.synopsis}</div>
          ) : null}
          {tags.length ? (
            <div className="pcv-candidate-meta-row">
              {tags.slice(0, showExpanded ? 8 : 5).map((t) => (
                <span key={t} className="pcv-candidate-tag">
                  {t}
                </span>
              ))}
            </div>
          ) : null}
        </div>
      </div>

      <div className="pcv-script-summary-cards">
        <div className="pcv-script-summary-card">
          <UserOutlined />
          <div>
            <span className="pcv-script-summary-label">角色</span>
            <span className="pcv-script-summary-value">{characters.length ? `${characters.length}个主要角色` : '—'}</span>
          </div>
        </div>
        <div className="pcv-script-summary-card">
          <EnvironmentOutlined />
          <div>
            <span className="pcv-script-summary-label">场景</span>
            <span className="pcv-script-summary-value">{scenes.length ? `${scenes.length}个场景` : '—'}</span>
          </div>
        </div>
        <div className="pcv-script-summary-card">
          <ClockCircleOutlined />
          <div>
            <span className="pcv-script-summary-label">剧本时长</span>
            <span className="pcv-script-summary-value">{formatDuration(script.target_duration_sec) || '—'}</span>
          </div>
        </div>
        <div className="pcv-script-summary-card">
          <FileTextOutlined />
          <div>
            <span className="pcv-script-summary-label">内容类型</span>
            <span className="pcv-script-summary-value">{contentType || '—'}</span>
          </div>
        </div>
      </div>

      {characters.length ? (
        <div className="pcv-script-characters-block">
          <div className="pcv-script-section-label">主要角色</div>
          <div className="pcv-script-character-row">
            {characters.slice(0, 4).map((name, idx) => {
              const ref = characterReferenceByName[name]
              const refAsset: ScriptSupportingAssetField | null = ref
                ? {
                    asset_id: ref.asset_id,
                    picker_item_id: ref.picker_item_id,
                    asset_type: 'image_asset',
                    display_name: ref.display_name || name,
                  }
                : null
              return (
                <div key={name} className="pcv-script-character-card">
                  <span className="pcv-script-character-avatar">
                    {refAsset && workspaceId ? <CharacterRefThumb asset={refAsset} workspaceId={workspaceId} /> : <UserOutlined />}
                  </span>
                  <div className="pcv-script-character-info">
                    <span className="pcv-script-character-name">{name}</span>
                    <span className="pcv-script-character-role">
                      {idx === 0 ? '主角' : '角色'}
                      {ref ? ' · 参考形象' : ''}
                    </span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      ) : null}

      {keyObjects.length ? (
        <div className="pcv-script-characters-block">
          <div className="pcv-script-section-label">核心物品</div>
          <div className="pcv-script-character-row">
            {keyObjects.slice(0, 4).map((name) => {
              const ref = productReferenceByName[name]
              const refAsset: ScriptSupportingAssetField | null = ref
                ? {
                    asset_id: ref.asset_id,
                    picker_item_id: ref.picker_item_id,
                    asset_type: 'image_asset',
                    display_name: ref.display_name || name,
                  }
                : null
              return (
                <div key={name} className="pcv-script-character-card">
                  <span className="pcv-script-character-avatar">
                    {refAsset && workspaceId ? <CharacterRefThumb asset={refAsset} workspaceId={workspaceId} fallback="object" /> : <FileTextOutlined />}
                  </span>
                  <div className="pcv-script-character-info">
                    <span className="pcv-script-character-name">{name}</span>
                    <span className="pcv-script-character-role">
                      道具
                      {ref?.relatedEntityName ? ` · ${ref.relatedEntityName}穿着参考` : ref ? ' · 视觉参考' : ''}
                    </span>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      ) : null}

      {scenes.length ? (
        <div className="pcv-script-scenes-preview">
          <div className="pcv-script-scenes-preview-header">
            <span className="pcv-script-section-label" style={{ margin: 0 }}>
              剧本片段预览（共 {scenes.length} 场景）
            </span>
            <button type="button" className="pcv-script-view-full-link nodrag" onClick={onOpenFull}>
              查看完整剧本 <RightOutlined />
            </button>
          </div>
          {visibleScenes.map((scene, idx) => {
            const { start, end } = sceneStartEnd(scenes, idx)
            const firstDialogue = (scene.dialogue || [])[0]
            return (
              <div key={scene.scene_number} className={`pcv-script-scene-preview-row${idx === 0 ? ' is-open' : ''}`}>
                <div className="pcv-script-scene-preview-meta">
                  <span className="pcv-script-scene-preview-index">场景{scene.scene_number}</span>
                  <span className="pcv-script-scene-preview-time">{formatTimecode(start, end)}</span>
                  <span className="pcv-script-scene-preview-title">{scene.setting || scene.dramatic_purpose || `场景 ${scene.scene_number}`}</span>
                </div>
                {idx === 0 || scenesExpanded ? (
                  <div className="pcv-script-scene-preview-body">
                    {scene.action ? <div className="pcv-script-scene-preview-action">{scene.action}</div> : null}
                    {firstDialogue ? (
                      <div className="pcv-script-scene-preview-dialogue">
                        <strong>{firstDialogue.character}{scene.voiceover ? '（旁白）' : ''}：</strong>
                        “{firstDialogue.line}”
                      </div>
                    ) : scene.voiceover ? (
                      <div className="pcv-script-scene-preview-dialogue">
                        <strong>旁白：</strong>“{scene.voiceover}”
                      </div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            )
          })}
          {!scenesExpanded && scenes.length > PREVIEW_SCENE_LIMIT ? (
            <button type="button" className="pcv-script-expand-scenes nodrag" onClick={() => setScenesExpanded(true)}>
              … 展开更多场景（共 {scenes.length} 个）
            </button>
          ) : null}
        </div>
      ) : (
        <button type="button" className="pcv-script-view-full-link nodrag" onClick={onOpenFull} style={{ alignSelf: 'flex-start' }}>
          查看完整剧本 <RightOutlined />
        </button>
      )}
    </div>
  )
}

function ScriptGenerationNodeImpl({ id, data, selected }: NodeProps) {
  const { t } = useTranslation('canvas')
  const nodeData = data as unknown as FlowNodeData & { workspaceId?: string; creativeContextLiveFingerprint?: string }
  const def = useNodeDefinition(nodeData.nodeType)
  const workspaceId = nodeData.workspaceId || ''
  const fields = nodeData.fields || {}
  const isDetailExpanded = Boolean(nodeData.isDetailExpanded)
  const showExpanded = functionCardShowsInspector(isDetailExpanded, nodeData.isDragTempCollapsed)

  const scriptSource = resolveScriptSourceContext(fields)
  const supportingAssets = Array.isArray(fields.supportingAssets) ? (fields.supportingAssets as ScriptSupportingAssetField[]) : []
  const scriptSourceStale =
    Boolean(scriptSource) &&
    Boolean(nodeData.creativeContextLiveFingerprint) &&
    scriptSource!.fingerprint !== nodeData.creativeContextLiveFingerprint
  const scriptSourcePreview = scriptSource ? scriptSourcePreviewText(scriptSource) : ''
  const scriptSourceMetaBits = [
    scriptSource ? scriptSourceSourceLabel(scriptSource, t) : null,
    scriptSource?.storyDirection ? String(scriptSource.storyDirection).slice(0, 24) : null,
    Array.isArray(scriptSource?.tags) && scriptSource!.tags!.length
      ? scriptSource!.tags!.slice(0, 3).join(' · ')
      : null,
  ].filter(Boolean) as string[]
  const contentTypeRaw = normalizeContentType((fields.contentType as string | null) ?? null)
  const scriptStructure = (fields.scriptStructure as string | null) ?? null
  const generationRequirements = Array.isArray(fields.generationRequirements) ? (fields.generationRequirements as string[]) : []
  const additionalRequirements = String(fields.additionalRequirements ?? '')
  const fieldDefaults = normalizeScriptGenerationFieldDefaults(fields)
  const contentType = contentTypeRaw || fieldDefaults.contentType
  const targetDurationSeconds =
    typeof fields.targetDurationSeconds === 'number' ? (fields.targetDurationSeconds as number) : fieldDefaults.targetDurationSeconds
  const skillSelectionSource = (fields.skillSelectionSource as string) || 'AUTO'
  const providerSelectionMode = normalizeProviderSelectionMode(fields.providerSelectionMode)
  const storedProviderOptionId = (fields.providerOptionId as string | null) ?? null
  const scriptResult = (fields.scriptResult as ScriptArtifact | null) ?? null
  const generationHistory = Array.isArray(fields.generationHistory)
    ? (fields.generationHistory as Array<{ script: ScriptArtifact; version: number; completedAt?: string }>)
    : []
  const runStatus = (fields.runStatus as string | null) ?? null
  const runErrorMessage = (fields.runErrorMessage as string | null) ?? null
  const divergeRunStatus = (fields.divergeRunStatus as string | null) ?? null
  const divergeRunErrorMessage = (fields.divergeRunErrorMessage as string | null) ?? null

  const [dragOver, setDragOver] = useState<'accept' | 'reject' | null>(null)
  const nodeShellRef = useRef<HTMLDivElement | null>(null)
  const updateNodeInternals = useUpdateNodeInternals()
  const [detailOpen, setDetailOpen] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [moreOpen, setMoreOpen] = useState(false)
  const [resultHovered, setResultHovered] = useState(false)
  const [customDurationDraft, setCustomDurationDraft] = useState('')

  // Persist visual defaults into node state so request builder and UI never diverge
  // (select showing 30s while fields.targetDurationSeconds is null).
  useEffect(() => {
    const patch: Record<string, unknown> = {}
    if (!(typeof fields.contentType === 'string' && fields.contentType.trim())) {
      patch.contentType = fieldDefaults.contentType
    }
    if (!(typeof fields.targetDurationSeconds === 'number' && fields.targetDurationSeconds > 0)) {
      patch.targetDurationSeconds = fieldDefaults.targetDurationSeconds
    }
    if (Object.keys(patch).length === 0) return
    const next = patchCanvasFieldsIfChanged(fields, patch)
    if (next) nodeData.onChangeFields?.(id, next)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only heal missing defaults once fields lack them
  }, [fieldDefaults.contentType, fieldDefaults.targetDurationSeconds, fields.contentType, fields.targetDurationSeconds, id])

  // Heal: when Creative text names characters/products but supportingAssets
  // never received derived chips (legacy nodes / empty Brief assets), surface
  // them so UI and request stay aligned with execution grounding.
  // Also drop incomplete entity fragments and coalesce legacy fields.assets
  // image edges into supportingAssets (RELATED_ASSET dual-field fix).
  useEffect(() => {
    const ctx = fields.creativeContext as ScriptCreativeContextField | null
    if (!ctx?.title && !ctx?.premise) return
    const derived = deriveNamedEntityAssetsFromCreative(ctx)
    const existing = coalesceScriptSupportingAssets(fields).filter((a) => !isIncompleteEntityFragment(a.display_name))
    const merged = derived.length
      ? mergeSupportingAssetsOnCreativeLink(existing, derived, null)
      : existing
    const bindings = inferEntityAssetBindings({
      title: ctx.title,
      premise: ctx.premise,
      characterRole: ctx.characterRole,
      productRole: ctx.productRole,
      hook: ctx.hook,
      storyDirection: ctx.storyDirection,
      rationale: ctx.rationale,
      supportingAssets: merged,
    })
    const stamped = applyBindingsToSupportingAssets(merged, bindings)
    if (
      stamped.length === (Array.isArray(fields.supportingAssets) ? (fields.supportingAssets as ScriptSupportingAssetField[]).length : 0) &&
      stamped.every((a, i) => {
        const prev = (fields.supportingAssets as ScriptSupportingAssetField[] | undefined)?.[i]
        return (
          prev &&
          a.display_name === prev.display_name &&
          a.asset_type === prev.asset_type &&
          a.asset_id === prev.asset_id &&
          a.boundEntityName === prev.boundEntityName
        )
      })
    ) {
      return
    }
    const next = patchCanvasFieldsIfChanged(fields, { supportingAssets: stamped })
    if (next) nodeData.onChangeFields?.(id, next)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- heal missing derived chips once
  }, [id, fields.creativeContext, fields.supportingAssets, fields.assets])

  const { options: providerOptions, error: providerOptionsError, loading: providerOptionsLoading } =
    useCapabilityProviderOptions(workspaceId, SCRIPT_GENERATION_CAPABILITY, 'script_generation')
  const resolvedProviderOptionId = useMemo(
    () => resolveCapabilityProviderOptionId({ selectionMode: providerSelectionMode, storedProviderOptionId, options: providerOptions }),
    [providerOptions, providerSelectionMode, storedProviderOptionId],
  )
  const providerSelectOptions = useMemo(
    () =>
      providerOptions.map((opt) => {
        const available = isProviderOptionSelectable(opt)
        const base = opt.frontend_display_name
        return {
          value: opt.provider_option_id,
          disabled: !available,
          label: available ? base : `${base}${t('generation.engineUnavailableSuffix')}`,
          title: opt.frontend_description || base,
        }
      }),
    [providerOptions, t],
  )

  const isGenerating = ACTIVE_RUN_STATUSES.has(runStatus || '')
  const isDiverging = ACTIVE_RUN_STATUSES.has(divergeRunStatus || '')
  const isBusy = isGenerating || isDiverging
  const needsRegeneration = computeNeedsRegeneration(fields, nodeData.creativeContextLiveFingerprint)

  const readiness = useMemo(
    () =>
      evaluateScriptGenerationReadiness({
        fields,
        providerOptions,
        providerLoading: providerOptionsLoading,
        providerError: providerOptionsError,
        isBusy,
      }),
    [fields, isBusy, providerOptions, providerOptionsError, providerOptionsLoading],
  )
  const primaryBlock = useMemo(() => primaryBlockingReason(readiness), [readiness])
  const blockingHint = primaryBlock ? t(primaryBlock.messageKey) : null
  const canGenerate = readiness.ready
  const canDiverge = readiness.ready

  const patchFields = useCallback(
    (patch: Record<string, unknown>) => {
      const next = patchCanvasFieldsIfChanged(fields, patch)
      if (!next) return
      nodeData.onChangeFields?.(id, next)
    },
    [fields, id, nodeData],
  )

  const onDragOverContext = (e: ReactDragEvent) => {
    if (!e.dataTransfer.types.includes(CREATIVE_CONTEXT_DND_TYPE)) {
      setDragOver('reject')
      return
    }
    if (showExpanded) {
      const inScriptSource = Boolean(
        (e.target as HTMLElement | null)?.closest?.(`[data-testid="${SCRIPT_SOURCE_SECTION_TESTID}"]`),
      )
      if (!inScriptSource) {
        setDragOver('reject')
        return
      }
    }
    e.preventDefault()
    e.dataTransfer.dropEffect = 'copy'
    setDragOver('accept')
  }
  const onDragLeaveContext = () => setDragOver(null)
  const onDropContext = (e: ReactDragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setDragOver(null)
    const raw = e.dataTransfer.getData(CREATIVE_CONTEXT_DND_TYPE)
    if (!raw) {
      message.error(t('scriptGeneration.dropCreativeRequired'))
      return
    }
    try {
      const payload = JSON.parse(raw)
      nodeData.onLinkCreativeContext?.(id, payload)
    } catch {
      message.error(t('scriptGeneration.dropCreativeRequired'))
    }
  }

  const openSupportingAssetPicker = () => {
    nodeData.onOpenAssetPicker?.({
      nodeId: id,
      purpose: 'generic',
      title: '添加关联资产',
      selectionMode: 'multiple',
      maxSelection: 20,
      initialSelection: supportingAssets.map((a) => a.picker_item_id || (a.asset_id ? `sa:${a.asset_id}` : '')).filter(Boolean),
      onConfirm: (selections: ImageAssetPickerSelection[]) => {
        const next: ScriptSupportingAssetField[] = selections.map((s) => ({
          asset_id: s.assetId,
          picker_item_id: s.pickerItemId,
          asset_type: 'image_asset',
          display_name: s.filename || s.assetId,
          inherited: false,
          source: 'manual',
          sourceNodeId: null,
          sourceResultId: null,
        }))
        patchFields({ supportingAssets: next })
      },
    })
  }
  const removeSupportingAsset = (asset: ScriptSupportingAssetField) => {
    const key = asset.asset_id || asset.display_name
    patchFields({ supportingAssets: supportingAssets.filter((a) => (a.asset_id || a.display_name) !== key) })
  }

  const pickDuration = (value: number | null) => {
    patchFields({ targetDurationSeconds: value })
  }
  const commitCustomDuration = () => {
    const n = Number(customDurationDraft)
    if (Number.isFinite(n) && n > 0) patchFields({ targetDurationSeconds: Math.round(n) })
    setCustomDurationDraft('')
  }

  const canDuplicate = !isBusy

  const handleDuplicate = (e: ReactMouseEvent) => {
    e.stopPropagation()
    if (!canDuplicate) return
    nodeData.onDuplicateNode?.(id)
  }
  const handleDelete = (e: ReactMouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    nodeData.onRequestDelete?.(id, e.currentTarget)
  }
  const handleResultExpandClick = () => {
    nodeData.onExpandFunctionCardDetail?.(id)
  }

  const onDragStartScriptContext = (e: ReactDragEvent) => {
    const payload = buildScriptContextDragPayloadFromScriptNode(id, fields)
    if (!payload) {
      e.preventDefault()
      return
    }
    e.dataTransfer.setData(SCRIPT_CONTEXT_DND_TYPE, JSON.stringify(payload))
    e.dataTransfer.effectAllowed = 'copy'
  }

  const handleQuickCreateStoryboard = (e: ReactMouseEvent) => {
    e.stopPropagation()
    nodeData.onQuickCreateStoryboardFromScript?.(id)
  }

  const generateStatusLabel = useMemo(() => {
    const status = isDiverging ? divergeRunStatus : runStatus
    if (status === 'SUBMITTING') return '正在提交…'
    if (status === 'QUEUED') return '排队中…'
    if (status === 'GENERATING') return scriptResult ? '正在重新生成…' : '正在生成剧本…'
    return null
  }, [divergeRunStatus, isDiverging, runStatus, scriptResult])

  const resultStatusBadge = isBusy ? '生成中' : scriptResult ? '已生成' : '未生成'
  const cardWidth = NODE_CARD_WIDTH
  const isCustomDurationActive =
    targetDurationSeconds != null && !DURATION_PRESETS.some((p) => p.value === targetDurationSeconds)

  useLayoutEffect(() => {
    updateNodeInternals(id)
  }, [
    id,
    showExpanded,
    scriptSource?.sourceNodeId,
    scriptSource?.title,
    scriptResult,
    moreOpen,
    supportingAssets.length,
    historyOpen,
    updateNodeInternals,
  ])

  useEffect(() => {
    const shell = nodeShellRef.current
    if (!shell || !showExpanded) return
    let frame = 0
    const schedule = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => updateNodeInternals(id))
    }
    schedule()
    const ro = new ResizeObserver(() => schedule())
    ro.observe(shell)
    return () => {
      ro.disconnect()
      cancelAnimationFrame(frame)
    }
  }, [id, showExpanded, updateNodeInternals])

  return (
    <div
      ref={nodeShellRef}
      className={`pcv-node pcv-node--script-generation${selected ? ' is-selected' : ''}${!showExpanded && dragOver === 'accept' ? ' is-drop-accept' : ''}${dragOver === 'reject' ? ' is-drop-reject' : ''} ${canvasNodeShellStateClass(showExpanded)}${resultHovered ? ' is-result-zone-active' : ''}`}
      style={{ width: cardWidth }}
      data-testid="canvas-script-generation-node"
      onDragOver={onDragOverContext}
      onDragLeave={onDragLeaveContext}
      onDrop={onDropContext}
    >
      {/* TASK-00058 forensic finding: `script-source-in` (rendered only
       * inside the ④ expanded body below) and `script-node-aggregate-in`
       * (rendered only here, collapsed) are DOM-mutually-exclusive by
       * `showExpanded` — a real, permanent design choice for the visible/
       * interactive handle a user can drag a NEW connection onto. The
       * bug was that a pre-existing PERSISTED edge targeting whichever
       * handle id is currently unmounted has no DOM `<Handle>` to
       * resolve, so `@xyflow/react` logs "Couldn't create edge for
       * target handle id" and keeps retrying on every store/viewport
       * update forever — confirmed live on Preview (workspace
       * 97161bbe.../project e7dad90a..., 101 nodes/82 edges) as 300+
       * warnings within 25s, and the underlying continuous-recompute
       * cost is what pushes large Canvases over React's synchronous
       * nested-update ceiling ("Maximum update depth exceeded", error
       * #185), tripping `CanvasErrorBoundary` and blanking the WHOLE
       * Canvas ("画布加载失败") even though every node/edge is valid data.
       * Fix: keep BOTH handle ids permanently mounted (never
       * conditionally unmount either) — the currently-inactive one is
       * rendered at the same anchor, just invisible/non-interactive, so
       * `@xyflow/react` can always resolve a real bounding rect for any
       * legacy or current edge, with zero visual/behavioral change. */}
      <Handle
        type="target"
        position={Position.Left}
        id={GENERATE_SCRIPT_NODE_AGGREGATE_HANDLE}
        className="pcv-handle pcv-handle--aggregate"
        data-testid="script-node-aggregate-in-handle"
        style={showExpanded ? { visibility: 'hidden', pointerEvents: 'none' } : undefined}
        aria-hidden={showExpanded || undefined}
      />
      {!showExpanded ? (
        <Handle
          type="target"
          position={Position.Left}
          id={GENERATE_SCRIPT_SOURCE_INPUT_HANDLE}
          className="pcv-handle pcv-handle--aggregate"
          style={{ visibility: 'hidden', pointerEvents: 'none' }}
          aria-hidden
        />
      ) : null}
      <Handle type="source" position={Position.Right} className="pcv-handle" />

      {/* ① Header — icon + title only; RIGHT History|Duplicate|Delete */}
      <CanvasFunctionCardHeader
        icon={def.icon}
        color={def.color}
        title={def.label}
        actions={
          <>
            <Tooltip title="生成历史" mouseEnterDelay={0.3}>
              <button type="button" className="pcv-media-hover-btn" onClick={() => setHistoryOpen((v) => !v)}>
                <HistoryOutlined />
              </button>
            </Tooltip>
            <Tooltip title={isBusy ? '正在生成，完成后可以复制' : '复制节点'} mouseEnterDelay={0.3}>
              <button type="button" className="pcv-media-hover-btn" disabled={!canDuplicate} onClick={handleDuplicate}>
                <CopyOutlined />
              </button>
            </Tooltip>
            <Tooltip title={t('board.deleteNodeConfirm')} mouseEnterDelay={0.3}>
              <button type="button" className="pcv-media-hover-btn pcv-media-hover-btn--danger" onClick={handleDelete}>
                <CloseOutlined />
              </button>
            </Tooltip>
          </>
        }
      />

      {historyOpen ? (
        <div className="pcv-script-history-panel nodrag" onClick={(e) => e.stopPropagation()}>
          <HistoryList entries={generationHistory} />
        </div>
      ) : null}

      {/* ② Result — ALWAYS visible (RESULT-FIRST) */}
      <div
        className={`pcv-gen-result-area pcv-script-result-area ${NODE_EXPAND_TRIGGER_CLASS}`}
        onMouseEnter={() => setResultHovered(true)}
        onMouseLeave={() => setResultHovered(false)}
        onClick={handleResultExpandClick}
      >
        <div className="pcv-gen-result-section-label">
          <span>生成结果</span>
          <span className={`pcv-gen-result-status-badge${scriptResult && !isBusy ? ' is-ready' : ''}${isBusy ? ' is-busy' : ''}`}>
            {resultStatusBadge}
          </span>
          {scriptResult && generationHistory.length ? (
            <button type="button" className="pcv-script-history-inline-link nodrag" onClick={() => setHistoryOpen(true)}>
              查看历史
            </button>
          ) : null}
        </div>

        {!scriptResult && !isBusy ? (
          <div className="pcv-gen-result-empty">
            <span className="pcv-gen-result-empty-icon">{def.icon}</span>
            <span>暂无剧本结果</span>
            <span className="pcv-gen-result-empty-hint">
              {scriptSource
                ? showExpanded
                  ? '请配置参数后点击「生成剧本」'
                  : '展开卡片配置参数，再点击「生成剧本」'
                : '请连接剧本源并配置参数后点击「生成剧本」'}
            </span>
            {scriptSource ? (
              <span className="pcv-script-context-chip" title={scriptSource.title}>
                {scriptSourceSourceLabel(scriptSource, t)}：{scriptSource.title}
              </span>
            ) : null}
          </div>
        ) : null}

        {isBusy ? (
          <div className="pcv-gen-result-loading">
            <LoadingOutlined spin />
            <span>{generateStatusLabel}</span>
          </div>
        ) : null}

        {!isBusy && scriptResult ? (
          <>
            {needsRegeneration ? (
              <div className="pcv-candidate-stale-banner">{t('scriptGeneration.inputChangedRegenerate')}</div>
            ) : null}
            <ScriptResultPresentation
              script={scriptResult}
              contentType={contentType}
              showExpanded={showExpanded}
              onOpenFull={() => setDetailOpen(true)}
              supportingAssets={supportingAssets}
              workspaceId={workspaceId}
              draggable
              onDragStart={onDragStartScriptContext}
            />
          </>
        ) : null}

        {!isDiverging && runStatus === 'ERROR' && runErrorMessage ? (
          <div className="pcv-gen-capability-warning pcv-gen-capability-warning--error">{runErrorMessage}</div>
        ) : null}
        {divergeRunStatus === 'ERROR' && divergeRunErrorMessage ? (
          <div className="pcv-gen-capability-warning pcv-gen-capability-warning--error">发散失败：{divergeRunErrorMessage}</div>
        ) : null}
      </div>

      {showExpanded ? (
        <div className="pcv-brief-body pcv-gen-input-body nodrag" onClick={(e) => e.stopPropagation()}>
          {/* ③ Primary Script Source (DOM-owned semantic input port) */}
          <div
            className={`pcv-brief-section pcv-video-input-section pcv-script-source-section${showExpanded && dragOver === 'accept' ? ' is-drop-target-active' : ''}`}
            data-testid={SCRIPT_SOURCE_SECTION_TESTID}
            data-script-input-port={GENERATE_SCRIPT_SOURCE_INPUT_HANDLE}
          >
            <VideoInputSectionHandle
              id={GENERATE_SCRIPT_SOURCE_INPUT_HANDLE}
              className="pcv-handle pcv-handle--script-source"
              testId="script-source-in-handle"
            />
            <span className="pcv-brief-section-label">
              {t('scriptGeneration.scriptSource')}{' '}
              <span className="pcv-gen-field-required" aria-hidden>*</span>
            </span>
            {scriptSource ? (
              <div className="pcv-script-context-compact" data-testid="script-context-card">
                <FileTextOutlined />
                <div className="pcv-script-context-compact-body">
                  <span className="pcv-script-context-compact-title">{scriptSource.title || t('scriptGeneration.creativeSourceMissing')}</span>
                  <span className="pcv-script-context-compact-meta">{scriptSourceMetaBits.join(' · ')}</span>
                  {scriptSourcePreview ? (
                    <span className="pcv-script-context-compact-premise">{scriptSourcePreview}</span>
                  ) : null}
                  {scriptSourceStale ? (
                    <span className="pcv-script-context-compact-stale">{t('scriptGeneration.creativeSourceUpdated')}</span>
                  ) : null}
                </div>
                <span className="pcv-script-context-compact-hint">{t('scriptGeneration.creativeSourceReplaceHint')}</span>
              </div>
            ) : (
              <div className="pcv-script-context-compact is-empty" data-testid="script-context-drop-target">
                {t('scriptGeneration.scriptSourceEmpty')}
              </div>
            )}
          </div>

          <div className="pcv-brief-section">
            <span className="pcv-brief-section-label">
              {t('scriptGeneration.supportingAssets')}（{supportingAssets.length}）{' '}
              <span className="pcv-brief-section-optional">（{t('scriptGeneration.supportingAssetsHint')}）</span>
            </span>
            <div className="pcv-brief-asset-row">
              {supportingAssets.map((a) => (
                <SupportingAssetChip key={a.asset_id || a.display_name} asset={a} workspaceId={workspaceId} onRemove={() => removeSupportingAsset(a)} />
              ))}
              <button type="button" className="pcv-brief-asset-add-btn nodrag" onClick={openSupportingAssetPicker}>
                <PlusOutlined /> {t('scriptGeneration.addAsset')}
              </button>
            </div>
          </div>

          {/* ④ Parameters — compact dropdowns, no chip wall */}
          <div className="pcv-gen-params-header">
            <span className="pcv-brief-section-label">生成参数</span>
            <button type="button" className="pcv-brief-collapse-toggle nodrag" onClick={() => setMoreOpen((v) => !v)}>
              {moreOpen ? '收起' : '更多参数'}
            </button>
          </div>
          <div className="pcv-gen-params-primary pcv-script-params-row">
            <div className="pcv-gen-field-row">
              <span className="pcv-gen-field-label">
                内容类型 <span className="pcv-gen-field-required" aria-hidden>*</span>
              </span>
              <CanvasSelect
                size="small"
                className="pcv-gen-field-select nodrag"
                popupClassName="pcv-gen-field-select-popup"
                value={contentType ?? undefined}
                placeholder="选择类型"
                options={CONTENT_TYPE_OPTIONS}
                onChange={(v) => patchFields({ contentType: v ?? null })}
                popupMatchSelectWidth={false}
                placement="bottomLeft"
                getPopupContainer={() => document.body}
              />
            </div>
            <div className="pcv-gen-field-row">
              <span className="pcv-gen-field-label">
                目标时长 <span className="pcv-gen-field-required" aria-hidden>*</span>
              </span>
              <CanvasSelect
                size="small"
                className="pcv-gen-field-select nodrag"
                popupClassName="pcv-gen-field-select-popup"
                value={targetDurationSeconds ?? undefined}
                placeholder="时长"
                options={DURATION_PRESETS.map((p) => ({ value: p.value, label: p.label }))}
                onChange={(v) => pickDuration(v ?? null)}
                popupMatchSelectWidth={false}
                placement="bottomLeft"
                getPopupContainer={() => document.body}
              />
            </div>
            <div className="pcv-gen-field-row">
              <span className="pcv-gen-field-label">剧本结构（可选）</span>
              <CanvasSelect
                size="small"
                className="pcv-gen-field-select nodrag"
                popupClassName="pcv-gen-field-select-popup"
                value={scriptStructure ?? undefined}
                placeholder="结构（可选）"
                allowClear
                options={SCRIPT_STRUCTURE_OPTIONS}
                onChange={(v) => patchFields({ scriptStructure: v ?? null })}
                onClear={() => patchFields({ scriptStructure: null })}
                popupMatchSelectWidth={false}
                placement="bottomLeft"
                getPopupContainer={() => document.body}
              />
            </div>
            <div className="pcv-gen-field-row">
              <span className="pcv-gen-field-label">重点方向（可选）</span>
              <CanvasSelect
                mode="multiple"
                size="small"
                className="pcv-gen-field-select nodrag"
                popupClassName="pcv-gen-field-select-popup"
                value={generationRequirements}
                placeholder="可选"
                options={GENERATION_REQUIREMENT_PRESETS.map((p) => ({ value: p, label: p }))}
                onChange={(v) => patchFields({ generationRequirements: v })}
                maxTagCount={2}
                popupMatchSelectWidth={false}
                placement="bottomLeft"
                getPopupContainer={() => document.body}
              />
            </div>
          </div>

          {moreOpen ? (
            <div className="pcv-brief-more-body">
              <div className="pcv-gen-field-row">
                <span className="pcv-gen-field-label">自定义时长（秒）</span>
                <input
                  className="pcv-brief-textarea nodrag"
                  style={{ minHeight: 30, height: 30, padding: '5px 8px' }}
                  value={isCustomDurationActive ? String(targetDurationSeconds ?? '') : customDurationDraft}
                  placeholder="例如 45"
                  onChange={(e) => setCustomDurationDraft(e.target.value.replace(/[^0-9]/g, ''))}
                  onBlur={commitCustomDuration}
                  onKeyDown={(e) => e.key === 'Enter' && commitCustomDuration()}
                />
              </div>
            </div>
          ) : null}

          {/* ⑤ Prompt — last input section */}
          <div className="pcv-brief-section">
            <span className="pcv-brief-section-label">补充说明（可选）</span>
            <textarea
              className="pcv-brief-textarea nodrag"
              placeholder="请参考创意的核心概念，突出产品的高端质感，加入反转情节，结尾强化品牌调性……"
              value={additionalRequirements}
              maxLength={ADDITIONAL_REQUIREMENTS_MAX}
              onChange={(e) => patchFields({ additionalRequirements: e.target.value })}
            />
            <span className="pcv-brief-textarea-counter">
              {additionalRequirements.length}/{ADDITIONAL_REQUIREMENTS_MAX}
            </span>
          </div>

          {/* ⑥ Footer — Provider | Skill | Diverge | Generate */}
          <div className="pcv-gen-footer nodrag" onClick={(e) => e.stopPropagation()}>
            <Tooltip title={providerSelectOptions.find((o) => o.value === resolvedProviderOptionId)?.title || 'Provider'} mouseEnterDelay={0.4}>
              <CanvasSelect
                size="small"
                className="pcv-gen-field-select pcv-gen-footer-select nodrag"
                popupClassName="pcv-gen-field-select-popup"
                value={resolvedProviderOptionId ?? undefined}
                disabled={providerOptionsError || providerSelectOptions.length === 0}
                placeholder={providerOptionsError ? '加载失败' : 'Provider'}
                options={providerSelectOptions}
                onChange={(v) => patchFields(buildExplicitProviderSelectionPatch(v))}
                popupMatchSelectWidth={false}
                placement="bottomLeft"
                getPopupContainer={() => document.body}
              />
            </Tooltip>
            <CanvasSelect
              size="small"
              className="pcv-gen-field-select pcv-gen-footer-select nodrag"
              popupClassName="pcv-gen-field-select-popup"
              value={skillSelectionSource === 'EXPLICIT' ? 'script_generation' : 'AUTO'}
              options={[
                { value: 'AUTO', label: '自动（推荐）' },
                { value: 'script_generation', label: '剧本生成 v1' },
              ]}
              onChange={(v) =>
                patchFields(v === 'AUTO' ? { skillSelectionSource: 'AUTO', skillKey: null } : { skillSelectionSource: 'EXPLICIT', skillKey: v })
              }
              popupMatchSelectWidth={false}
              placement="bottomLeft"
              getPopupContainer={() => document.body}
            />
            <Tooltip title="发散生成 — 基于当前内容一次生成 3 个方向明显不同的候选方案">
              <button
                type="button"
                className="pcv-gen-footer-diverge-btn pcv-gen-footer-diverge-btn--labeled"
                disabled={!canDiverge}
                onClick={() => {
                  if (!canDiverge) return
                  nodeData.onDivergeScript?.(id)
                }}
              >
                {isDiverging ? <LoadingOutlined spin /> : <BranchesOutlined />} 发散生成
              </button>
            </Tooltip>
            <Tooltip
              title={
                !canGenerate && blockingHint
                  ? blockingHint
                  : !canGenerate
                    ? t('scriptGeneration.completeRequiredToGenerate')
                    : undefined
              }
              mouseEnterDelay={0.2}
            >
              <button
                type="button"
                className="pcv-gen-footer-generate-btn"
                data-testid="script-generate-btn"
                disabled={!canGenerate}
                onClick={() => {
                  if (!canGenerate) return
                  nodeData.onGenerateScript?.(id)
                }}
              >
                {isGenerating ? <LoadingOutlined spin /> : null}{' '}
                {isGenerating ? '生成中…' : scriptResult ? '重新生成' : '生成剧本'}
              </button>
            </Tooltip>
          </div>
          {!canGenerate && blockingHint && !isBusy ? (
            <div className="pcv-gen-footer-hint" data-testid="script-readiness-hint">
              {scriptResult ? t('scriptGeneration.cannotRegenerateMissingInput') : blockingHint}
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Full Script Viewer — lazy mounted only when open */}
      {detailOpen ? <ScriptFullDetailModal script={scriptResult} open={detailOpen} onClose={() => setDetailOpen(false)} /> : null}

      {/* External Quick Actions — CARD RIGHT OUTSIDE the Result region
       * (spec Gate E), never nested inside the Script result content.
       * Admin-driven (Gate G) — renders nothing until Platform Admin has
       * this action enabled for `script_generation`. */}
      {!isBusy && scriptResult && nodeData.onQuickCreateStoryboardFromScript ? (
        <CanvasResultQuickActionRail
          sourceNodeType="script_generation"
          className="pcv-media-workflow-toolbar--script-generation"
          onMouseEnter={() => setResultHovered(true)}
          onMouseLeave={() => setResultHovered(false)}
          actionHandlers={[
            {
              targetNodeType: 'storyboard_generation',
              tooltip: '生成分镜 — 用这个剧本结果作为剧本上下文',
              onClick: handleQuickCreateStoryboard,
            },
          ]}
        />
      ) : null}
    </div>
  )
}

export default memo(ScriptGenerationNodeImpl)
